<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import wootConstants from 'dashboard/constants/globals';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import KanbanFilterDropdown from './KanbanFilterDropdown.vue';
import { KANBAN_DEFAULT_SORT, KANBAN_SORT_OPTIONS } from '../constants';

const props = defineProps({
  isRefreshing: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['refresh', 'create-column']);

const assigneeType = defineModel('assigneeType', {
  type: String,
  default: wootConstants.ASSIGNEE_TYPE.ALL,
});
const inboxId = defineModel('inboxId', { type: Number, default: null });
const teamId = defineModel('teamId', { type: Number, default: null });
const label = defineModel('label', { type: String, default: '' });
const sortBy = defineModel('sortBy', {
  type: String,
  default: wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
});
const searchQuery = defineModel('searchQuery', { type: String, default: '' });

const { t } = useI18n();
const inboxes = useMapGetter('inboxes/getInboxes');
const teams = useMapGetter('teams/getTeams');
const labels = useMapGetter('labels/getLabels');

const openMenu = ref(null);

const assigneeTabs = computed(() => [
  {
    value: wootConstants.ASSIGNEE_TYPE.ME,
    label: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.me'),
  },
  {
    value: wootConstants.ASSIGNEE_TYPE.UNASSIGNED,
    label: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.unassigned'),
  },
  {
    value: wootConstants.ASSIGNEE_TYPE.ALL,
    label: t('CHAT_LIST.ASSIGNEE_TYPE_TABS.all'),
  },
]);

const activeAssigneeTab = computed(() =>
  Math.max(
    0,
    assigneeTabs.value.findIndex(tab => tab.value === assigneeType.value)
  )
);

const selectedInbox = computed(() =>
  inboxes.value.find(item => item.id === inboxId.value)
);
const selectedTeam = computed(() =>
  teams.value.find(item => item.id === teamId.value)
);
const selectedLabel = computed(() =>
  labels.value.find(item => item.title === label.value)
);

const inboxLabel = computed(
  () => selectedInbox.value?.name || t('KANBAN.INBOX')
);
const teamLabel = computed(() => selectedTeam.value?.name || t('KANBAN.TEAM'));
const labelFilterLabel = computed(
  () => selectedLabel.value?.title || t('KANBAN.LABEL')
);
const sortLabel = computed(() =>
  t(`CHAT_LIST.SORT_ORDER_ITEMS.${sortBy.value}.TEXT`)
);

const hasExtraFilters = computed(
  () =>
    Boolean(inboxId.value) ||
    Boolean(teamId.value) ||
    Boolean(label.value) ||
    Boolean(searchQuery.value) ||
    sortBy.value !== KANBAN_DEFAULT_SORT
);

const inboxItems = computed(() => [
  {
    label: t('KANBAN.ALL_INBOXES'),
    value: null,
    action: 'inbox',
    isSelected: !inboxId.value,
  },
  ...inboxes.value.map(item => ({
    label: item.name,
    value: item.id,
    action: 'inbox',
    icon: getInboxIconByType(
      item.channel_type || item.channelType,
      item.medium
    ),
    isSelected: inboxId.value === item.id,
  })),
]);

const teamItems = computed(() => [
  {
    label: t('KANBAN.ALL_TEAMS'),
    value: null,
    action: 'team',
    isSelected: !teamId.value,
  },
  ...teams.value.map(item => ({
    label: item.name,
    value: item.id,
    action: 'team',
    isSelected: teamId.value === item.id,
  })),
]);

const labelItems = computed(() => [
  {
    label: t('KANBAN.ALL_LABELS'),
    value: '',
    action: 'label',
    isSelected: !label.value,
  },
  ...labels.value.map(item => ({
    label: item.title,
    value: item.title,
    action: 'label',
    isSelected: label.value === item.title,
  })),
]);

const sortItems = computed(() =>
  KANBAN_SORT_OPTIONS.map(value => ({
    label: t(`CHAT_LIST.SORT_ORDER_ITEMS.${value}.TEXT`),
    value,
    action: 'sort',
    isSelected: sortBy.value === value,
  }))
);

const toggleMenu = name => {
  openMenu.value = openMenu.value === name ? null : name;
};

const closeOnOutside = name => {
  if (openMenu.value === name) openMenu.value = null;
};

const onAssigneeTabChanged = tab => {
  assigneeType.value = tab.value;
};

const applyFilter = ({ action, value }) => {
  openMenu.value = null;
  if (action === 'inbox') inboxId.value = value;
  if (action === 'team') teamId.value = value;
  if (action === 'label') label.value = value;
  if (action === 'sort') sortBy.value = value;
};

const clearFilters = () => {
  inboxId.value = null;
  teamId.value = null;
  label.value = '';
  searchQuery.value = '';
  sortBy.value = KANBAN_DEFAULT_SORT;
  openMenu.value = null;
};
</script>

<template>
  <header
    class="flex flex-col gap-3 border-b border-n-weak bg-n-solid-1 px-4 py-3"
  >
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div class="min-w-0">
        <h1 class="text-base font-medium text-n-slate-12">
          {{ $t('KANBAN.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ $t('KANBAN.DESCRIPTION') }}
        </p>
      </div>
      <div
        class="flex min-w-0 flex-1 items-center justify-end gap-2 sm:max-w-md"
      >
        <Input
          v-model="searchQuery"
          type="search"
          size="sm"
          :placeholder="$t('KANBAN.SEARCH_PLACEHOLDER')"
          :custom-input-class="[
            'h-8 ltr:!pl-8 rtl:!pr-8 bg-n-alpha-2 dark:bg-n-solid-2',
          ]"
          class="w-full"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="absolute top-1/2 size-4 -translate-y-1/2 text-n-slate-11 ltr:left-2 rtl:right-2"
            />
          </template>
        </Input>
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-refresh-cw"
          class="shrink-0"
          :is-loading="props.isRefreshing"
          :aria-label="$t('KANBAN.REFRESH')"
          @click="emit('refresh')"
        />
      </div>
    </div>
    <div class="flex flex-wrap items-center gap-2">
      <TabBar
        :tabs="assigneeTabs"
        :initial-active-tab="activeAssigneeTab"
        @tab-changed="onAssigneeTabChanged"
      />
      <div class="hidden h-3.5 w-px bg-n-strong sm:block" />
      <div class="flex flex-wrap items-center gap-2">
        <KanbanFilterDropdown
          icon="i-lucide-inbox"
          :label="inboxLabel"
          :is-active="Boolean(inboxId)"
          :is-open="openMenu === 'inbox'"
          :menu-items="inboxItems"
          show-search
          @toggle="toggleMenu('inbox')"
          @close="closeOnOutside('inbox')"
          @action="applyFilter"
        />
        <KanbanFilterDropdown
          v-if="teams.length"
          icon="i-lucide-users"
          :label="teamLabel"
          :is-active="Boolean(teamId)"
          :is-open="openMenu === 'team'"
          :menu-items="teamItems"
          show-search
          @toggle="toggleMenu('team')"
          @close="closeOnOutside('team')"
          @action="applyFilter"
        />
        <KanbanFilterDropdown
          v-if="labels.length"
          icon="i-lucide-tag"
          :label="labelFilterLabel"
          :is-active="Boolean(label)"
          :is-open="openMenu === 'label'"
          :menu-items="labelItems"
          show-search
          @toggle="toggleMenu('label')"
          @close="closeOnOutside('label')"
          @action="applyFilter"
        />
        <KanbanFilterDropdown
          icon="i-lucide-arrow-down-up"
          :label="sortLabel"
          :is-active="sortBy !== KANBAN_DEFAULT_SORT"
          :is-open="openMenu === 'sort'"
          :menu-items="sortItems"
          @toggle="toggleMenu('sort')"
          @close="closeOnOutside('sort')"
          @action="applyFilter"
        />
        <Button
          v-if="hasExtraFilters"
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-x"
          :label="$t('KANBAN.CLEAR_FILTERS')"
          class="text-n-slate-11"
          @click="clearFilters"
        />
        <Button
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-plus"
          :label="$t('KANBAN.NEW_COLUMN')"
          @click="emit('create-column')"
        />
      </div>
    </div>
  </header>
</template>
